/**
 * Coveo Content Engine — lazy singleton.
 *
 * Uses the classic (@coveo/headless) engine because the Commerce headless engine
 * does not yet support content/document search.
 *
 * Handles: Solutions & Services tab, content facets, content pagination.
 */
import { setGlobalDevModeChecks } from 'reselect'
import {
  buildSearchEngine,
  buildSearchBox as buildClassicSearchBox,
  buildResultList,
  buildPager,
  buildResultsPerPage,
  buildFacet,
  buildFacetManager,
  buildAutomaticFacetGenerator,
  buildBreadcrumbManager,
  buildDidYouMean,
  buildQuerySummary,
  buildUrlManager,
} from '@coveo/headless'
import {
  getContentFragment,
  isCoveoAnalyticsEnabled,
  CONTENT_CHECKBOX_FACETS,
  CONTENT_RESULT_FIELDS
} from '../../shared/coveoUtils'
import { getCoveoClassicToken } from '../../api'

const buildCheckboxFacet = (engine, field) => buildFacet(engine, {
  options: {
    field,
    facetId: field,
    numberOfValues: 10,
    sortCriteria: 'occurrences',
    facetSearch: {
      numberOfValues: 10,
    },
  },
})

// Reselect v5 (used internally by @coveo/headless) runs an input-stability check in dev
// mode. Classic headless selectors can return new object/array references on every call,
// causing false-positive "input selector returned different result" errors.
// setGlobalDevModeChecks is a global setting — setting it here keeps this file
// self-contained in case it is ever initialised independently of commerceEngine.
setGlobalDevModeChecks({ inputStabilityCheck: 'never' })

// ---------------------------------------------------------------------------
// Lazy singleton
// ---------------------------------------------------------------------------
let _contentInstance = null

/**
 * Lazily creates (or returns the existing) classic headless engine + controllers
 * for content search tabs (e.g., Solutions & Services).
 */
export function initContentEngine(coveoData) {
  if (_contentInstance) return _contentInstance

  if (typeof window === 'undefined') {
    console.warn('Coveo content engine requires a browser environment — skipping initialization')
    return null
  }

  const { coveoClassicToken: token, organizationId, trackingId, searchHub } = coveoData || {};
  if (!token || !organizationId || !trackingId || !searchHub) {
    console.warn('Coveo content engine requires token, organizationId, trackingId, and searchHub — skipping initialization')
    return null
  }

  const engine = buildSearchEngine({
    configuration: {
      organizationId: organizationId,
      accessToken: token,
      renewAccessToken: getCoveoClassicToken,
      analytics: {
        trackingId: trackingId,
        enabled: isCoveoAnalyticsEnabled(),
        analyticsMode: 'legacy',
      },
      search: {
        searchHub: searchHub,
      },
    },
  })

  // clearFilters defaults to true, which makes submit() dispatch deselectAllBreadcrumbs()
  // right before building the search request — this wipes out the automatic-facet
  // selections that buildUrlManager's constructor just restored from the URL (below),
  // so the very first request (and any subsequent submit()) would go out with no
  // facets applied. Facet clearing is already handled explicitly elsewhere
  // (clearContentFilters/contentBreadcrumbManager.deselectAll()), so disable it here.
  const searchBox      = buildClassicSearchBox(engine, { options: { numberOfSuggestions: 8, clearFilters: false } })
  const resultList     = buildResultList(engine, { options: { fieldsToInclude: CONTENT_RESULT_FIELDS } })
  const pager          = buildPager(engine)
  const resultsPerPage = buildResultsPerPage(engine, { initialState: { numberOfResults: 50 } })
  const facetManager   = buildFacetManager(engine)
  const contentFacets  = CONTENT_CHECKBOX_FACETS.map((field) => buildCheckboxFacet(engine, field))
  const facetGenerator = buildAutomaticFacetGenerator(engine, { options: { numberOfAutomaticFacets: 8 } })
  const breadcrumbManager = buildBreadcrumbManager(engine)
  const didYouMean     = buildDidYouMean(engine)
  const summary        = buildQuerySummary(engine)
  // Content facets (shown in the URL as af-*, restored here as the manual ones' native
  // f-* form so classic headless can restore selections into facetSet at construction
  // time) <-> URL sync, mirroring the commerce engine's urlManager.
  const urlManager     = buildUrlManager(engine, { initialState: { fragment: getContentFragment(CONTENT_CHECKBOX_FACETS) } })

  _contentInstance = {
    engine,
    searchBox,
    resultList,
    pager,
    resultsPerPage,
    facetManager,
    contentFacets,
    facetGenerator,
    breadcrumbManager,
    didYouMean,
    summary,
    urlManager,
  }

  return _contentInstance
}
